import {
  Avatar,
  CollapseCard,
  FlexBox,
  NavigationButton,
} from '@dixa/lib-shared-habla';
import {
  MetaConversation,
  PreviousConversation,
} from 'lib-shared-legacy-types';
import { ampli } from 'lib-shared-tracking';
import { User } from 'lib-shared-types-api';
import React, { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { getChannelName } from 'app/utils/channelName';
import { createSearchUrl } from 'app/views/layout/views/search/utils';
import { getConversationTitle } from 'components/conversation/conversationDetails/helpers';

import '../../../../styles/cards.less';

import { HistoryCardItem } from './historyCardItem/historyCardItem';

type HistoryCardProps = {
  requester: User;
  previousConversations: PreviousConversation[];
  isOpen: boolean;
  isMinimized?: boolean;
  onToggle: () => void;
};

export const HistoryCard = memo(function HistoryCard({
  isOpen,
  onToggle,
  previousConversations,
  requester,
  isMinimized = false,
}: HistoryCardProps): JSX.Element | null {
  const navigate = useNavigate();

  const renderTitle = useCallback(
    ({ metaConversation }: { metaConversation: MetaConversation }) => {
      const subject = getConversationTitle(metaConversation).subject;
      const channelName = metaConversation.conversation.initial_channel
        ? getChannelName(metaConversation.conversation.initial_channel)
        : null;

      return <span>{subject ? subject : channelName}</span>;
    },
    [],
  );

  const handleOnConversationLinkClick = useCallback((): void => {
    ampli.conversationSidebarConversationHistoryNavigatedToConversation();
  }, []);

  const handleOnToggle = useCallback(
    (isOpen: boolean): void => {
      if (isOpen) {
        ampli.conversationSidebarConversationHistoryOpened();
      } else {
        ampli.conversationSidebarConversationHistoryClosed();
      }

      onToggle();
    },
    [onToggle],
  );

  const avatar = <Avatar icon="history" />;

  if (isMinimized) {
    return <div className="icon-holder">{avatar}</div>;
  }

  let showAllConversationBtn: React.ReactNode = null;

  if (previousConversations.length > 3) {
    const { id } = requester;

    const searchUrlWithFilter = createSearchUrl('conversations', {
      filters: {
        strategy: 'all',
        conditions: [
          {
            field: 'requested_by',
            operator: 'is_one',
            values: [{ label: '', value: id }],
          },
        ],
      },
    });

    showAllConversationBtn = (
      <NavigationButton
        className="card-action-btn"
        navigate={() => navigate(searchUrlWithFilter)}
      >
        Show all conversations
      </NavigationButton>
    );
  }

  if (!previousConversations.length) {
    return null;
  }

  return (
    <CollapseCard onToggle={handleOnToggle} isOpen={isOpen}>
      <CollapseCard.Header avatar={avatar}>
        Conversation history
      </CollapseCard.Header>
      <CollapseCard.Content>
        {previousConversations
          .slice(0, 3)
          .map(({ conversation }: PreviousConversation, index: number) => {
            return (
              <HistoryCardItem
                key={conversation.id ?? index}
                conversation={conversation}
                handleOnConversationLinkClick={handleOnConversationLinkClick}
                renderTitle={renderTitle}
              />
            );
          })}
      </CollapseCard.Content>
      <CollapseCard.Content>
        <FlexBox justifyContent="center" alignItems="center" padding="200 0">
          {showAllConversationBtn}
        </FlexBox>
      </CollapseCard.Content>
    </CollapseCard>
  );
});
